import React from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { update_policy_config } from "services/apiSrv";
import { useParams } from "react-router-dom";
import { show_toast } from "utils/helpers";
import TableComponent from "components/widgets/table";
import { valuationConfigs } from "pages/valuation/valuersData";

// Validation schema
const validationSchema = Yup.object().shape({
  autoRenewalNotification: Yup.number().required("Auto renewal notification is required"),
  covernoteIssuance: Yup.string().required("Covernote issuance is required"),
  covernoteTemplate: Yup.string().when("covernoteIssuance", {
    is: "auto",
    then: (schema) => schema.required("Covernote template is required"),
  }),
  valuationRequired: Yup.boolean().required("Valuation required is required"),
  valuationTemplate: Yup.string().when("valuationRequired", {
    is: true,
    then: (schema) => schema.required("Valuation template is required"),
  }),
  valuers: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
    })
  ),
});

export default function PolicyConfigForm({refetchProductDetails}) {
  const { id: productSystemRef } = useParams();

  const initialValues = {
    autoRenewalNotification: 0,
    covernoteIssuance: "manual",
    covernoteTemplate: "",
    valuationRequired: false,
    valuationTemplate: "",
    valuerName: "",
    valuerEmail: "",
    valuers: [],
  };

  const autoRenewalOptions = [
    { value: 0, label: "Select Notification Period" },
    { value: 7, label: "7 Days Before" },
    { value: 14, label: "14 Days Before" },
    { value: 30, label: "30 Days Before" },
  ];

  const templateOptions = [
    {
      value: "template1",
      label: "Professional Template",
      description: "Standard corporate template with professional layout",
      icon: "📄",
    },
    {
      value: "template2",
      label: "Modern Template",
      description: "Contemporary design with enhanced visual elements",
      icon: "🎨",
    },
    {
      value: "template3",
      label: "Classic Template",
      description: "Traditional format with comprehensive details",
      icon: "📋",
    },
  ];




  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = { ...values, productSystemRef };
    console.log("Submitting Payload:", payload);
    try {
      await update_policy_config(payload);
      show_toast("Policy Configuration saved successfully!", "success");
      refetchProductDetails?.()
      resetForm();
    } catch (error) {
      console.error("API Error:", error);
      show_toast("Error saving policy configuration: " + error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const TemplateCard = ({ template, isSelected, onSelect }) => (
    <div
      className={`card h-100 cursor-pointer border ${
        isSelected ? "border-primary" : "border-light"
      } shadow-sm hover-shadow transition-all`}
      onClick={onSelect}
      style={{
        cursor: "pointer",
        transition: "all 0.2s ease",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 0 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-center mb-2">
          <span className="fs-4 me-2">{template.icon}</span>
          <h6 className="card-title mb-0 fw-semibold" style={{ color: "#555" }}>
            {template.label}
          </h6>
        </div>
        <p className="card-text text-muted small flex-grow-1">
          {template.description}
        </p>
        <div className="mt-auto">
          <div className={`form-check ${isSelected ? "text-primary" : ""}`}>
            <input
              className="form-check-input"
              type="radio"
              checked={isSelected}
              onChange={onSelect}
            />
            <label className="form-check-label small">
              {isSelected ? "Selected" : "Select this template"}
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="p-4 bg-white rounded shadow-sm my-4"
      style={{ borderRadius: "8px", boxShadow: "0 0 8px rgba(0, 0, 0, 0.1)" }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="mb-3">
            {/* Configuration Sections */}
            <div className="row mb-5">
              {/* Renewal Container (Left) */}
              <div className="col-lg-6 mb-4">
                <div
                  className="card h-100 border"
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 0 8px rgba(0, 0, 0, 0.1)",
                    borderColor: "#ddd",
                  }}
                >
                  <div
                    className="card-header"
                    style={{ background: "#f4f4f4", color: "#555", fontWeight: "600" }}
                  >
                    <h6 className="mb-0 fw-semibold">Auto Renewal Settings</h6>
                  </div>
                  <div className="card-body">
                    <label
                      htmlFor="autoRenewalNotification"
                      className="form-label fw-semibold"
                      style={{ color: "#555" }}
                    >
                      Notification Period
                    </label>
                    <Field
                      as="select"
                      name="autoRenewalNotification"
                      className="form-select form-control"
                      style={{ fontSize: "14px" }}
                    >
                      {autoRenewalOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="autoRenewalNotification"
                      component="div"
                      className="invalid-feedback d-block"
                      style={{ fontSize: "14px", color: "#dc3545" }}
                    />
                  </div>
                </div>
              </div>

              {/* Covernote Container (Right) */}
              <div className="col-lg-6 mb-4">
                <div
                  className="card h-100 border"
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 0 8px rgba(0, 0, 0, 0.1)",
                    borderColor: "#ddd",
                  }}
                >
                  <div
                    className="card-header"
                    style={{ background: "#f4f4f4", color: "#555", fontWeight: "600" }}
                  >
                    <h6 className="mb-0 fw-semibold">Covernote Configuration</h6>
                  </div>
                  <div className="card-body">
                    <label
                      className="form-label fw-semibold mb-3"
                      style={{ color: "#555" }}
                    >
                      Issuance Type
                    </label>
                    <div className="d-flex mb-4" style={{ gap: "1rem" }}>
                      <div className="form-check me-5">
                        <Field
                          type="radio"
                          name="covernoteIssuance"
                          value="auto"
                          className="form-check-input"
                        />
                        <label
                          className="form-check-label fw-medium"
                          style={{ color: "#555" }}
                        >
                          Auto
                        </label>
                      </div>
                      <div className="form-check me-5">
                        <Field
                          type="radio"
                          name="covernoteIssuance"
                          value="manual"
                          className="form-check-input"
                        />
                        <label
                          className="form-check-label fw-medium"
                          style={{ color: "#555" }}
                        >
                          Manual
                        </label>
                      </div>
                    </div>
                    <ErrorMessage
                      name="covernoteIssuance"
                      component="div"
                      className="invalid-feedback d-block"
                      style={{ fontSize: "14px", color: "#dc3545" }}
                    />

                    {values.covernoteIssuance === "auto" && (
                      <div className="mt-4">
                        <label
                          className="form-label fw-semibold mb-3"
                          style={{ color: "#555" }}
                        >
                          Select Template
                        </label>
                        <div className="row g-3">
                          {templateOptions.map((template) => (
                            <div key={template.value} className="col-12">
                              <TemplateCard
                                template={template}
                                isSelected={values.covernoteTemplate === template.value}
                                onSelect={() =>
                                  setFieldValue("covernoteTemplate", template.value)
                                }
                              />
                            </div>
                          ))}
                        </div>
                        <ErrorMessage
                          name="covernoteTemplate"
                          component="div"
                          className="invalid-feedback d-block mt-2"
                          style={{ fontSize: "14px", color: "#dc3545" }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Valuation Container (Full Width) */}
            <div className="mb-5">
              <div
                className="card border"
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 0 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#ddd",
                }}
              >
                <div
                  className="card-header"
                  style={{ background: "#f4f4f4", color: "#555", fontWeight: "600" }}
                >
                  <h6 className="mb-0 fw-semibold">Valuation Requirements</h6>
                </div>
                <div className="card-body">
                  <label
                    className="form-label fw-semibold mb-3"
                    style={{ color: "#555" }}
                  >
                    Is Valuation Required?
                  </label>
                  <div className="d-flex mb-4" style={{ gap: "1rem" }}>
                    <div className="form-check me-5">
                      <Field
                        type="radio"
                        name="valuationRequired"
                        value={true}
                        checked={values.valuationRequired === true} // Explicitly bind checked state
                        onChange={() => setFieldValue("valuationRequired", true)}
                        className="form-check-input"
                        
                      />
                      <label
                        className="form-check-label fw-medium"
                        style={{ color: "#555" }}
                      >
                        Yes
                      </label>
                    </div>
                    <div className="form-check me-5">
                      <Field
                        type="radio"
                        name="valuationRequired"
                        value={false}
                        checked={values.valuationRequired === false}
                        className="form-check-input"
                        onChange={() => setFieldValue("valuationRequired", false)}
                      />
                      <label
                        className="form-check-label fw-medium"
                        style={{ color: "#555" }}
                      >
                        No
                      </label>
                    </div>
                  </div>
                  <ErrorMessage
                    name="valuationRequired"
                    component="div"
                    className="invalid-feedback d-block"
                    style={{ fontSize: "14px", color: "#dc3545" }}
                  />

                  {values.valuationRequired && (
                    <div className="mt-4">
                      <div className="mb-5">
                        <label
                          className="form-label fw-semibold mb-3"
                          style={{ color: "#555" }}
                        >
                          Valuation Templates
                        </label>
                        <div className="row g-3">
                          {templateOptions.map((template) => (
                            <div key={template.value} className="col-lg-4 col-md-6">
                              <TemplateCard
                                template={template}
                                isSelected={values.valuationTemplate === template.value}
                                onSelect={() =>
                                  setFieldValue("valuationTemplate", template.value)
                                }
                              />
                            </div>
                          ))}
                        </div>
                        <ErrorMessage
                          name="valuationTemplate"
                          component="div"
                          className="invalid-feedback d-block mt-2"
                          style={{ fontSize: "14px", color: "#dc3545" }}
                        />
                      </div>

                      <div className="mt-4">
                        <label
                          className="form-label fw-semibold mb-3"
                          style={{ color: "#555" }}
                        >
                          Manage Valuers
                        </label>
                        <FieldArray name="valuers">
                          {({ push, remove }) => (
                            <div>
                              <div
                                className="card bg-light border mb-4"
                                style={{ borderRadius: "8px", borderColor: "#ddd" }}
                              >
                                <div className="card-body">
                                  <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                      <Field
                                        name="valuerName"
                                        placeholder="Valuer Name"
                                        className="form-control"
                                        style={{ fontSize: "14px" }}
                                      />
                                      <ErrorMessage
                                        name="valuerName"
                                        component="div"
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "14px", color: "#dc3545" }}
                                      />
                                    </div>
                                    <div className="col-md-4">
                                      <Field
                                        name="valuerEmail"
                                        placeholder="Email Address"
                                        className="form-control"
                                        style={{ fontSize: "14px" }}
                                      />
                                      <ErrorMessage
                                        name="valuerEmail"
                                        component="div"
                                        className="invalid-feedback d-block"
                                        style={{ fontSize: "14px", color: "#dc3545" }}
                                      />
                                    </div>
                                    <div className="col-md-4">
                                      <button
                                        type="button"
                                        className="btn btn-primary w-100"
                                        style={{ fontSize: "14px" }}
                                        onClick={() => {
                                          if (!values.valuerName || !values.valuerEmail) {
                                            return;
                                          }
                                          const newValuer = {
                                            name: values.valuerName,
                                            email: values.valuerEmail,
                                          };
                                          push(newValuer);
                                          setFieldValue("valuerName", "");
                                          setFieldValue("valuerEmail", "");
                                        }}
                                      >
                                        Add Valuer
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {values.valuers.length > 0 && (
                                <div
                                  className="card border"
                                  style={{
                                    borderRadius: "8px",
                                    boxShadow: "0 0 8px rgba(0, 0, 0, 0.1)",
                                    borderColor: "#ddd",
                                  }}
                                >
                                  <div
                                    className="card-header"
                                    style={{
                                      background: "#f4f4f4",
                                      color: "#555",
                                      fontWeight: "600",
                                    }}
                                  >
                                    <h6 className="mb-0">Added Valuers</h6>
                                  </div>
                                  <div className="card-body p-0">
                                    <div className="table-responsive">
                                      <table
                                        className="table table-hover mb-0"
                                        style={{ borderCollapse: "collapse" }}
                                      >
                                        <thead
                                          style={{
                                            background: "#f4f4f4",
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 10,
                                          }}
                                        >
                                          <tr>
                                            <th
                                              style={{
                                                padding: "12px",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                                borderBottom: "1px solid #ddd",
                                              }}
                                            >
                                              Name
                                            </th>
                                            <th
                                              style={{
                                                padding: "12px",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                                borderBottom: "1px solid #ddd",
                                              }}
                                            >
                                              Email
                                            </th>
                                            <th
                                              style={{
                                                padding: "12px",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                                borderBottom: "1px solid #ddd",
                                                textAlign: "center",
                                              }}
                                            >
                                              Actions
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {values.valuers.map((valuer, index) => (
                                            <tr key={index}>
                                              <td
                                                style={{
                                                  padding: "12px",
                                                  fontWeight: 500,
                                                  fontSize: "14px",
                                                  borderBottom: "1px solid #ddd",
                                                }}
                                              >
                                                {valuer.name}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "12px",
                                                  color: "#6c757d",
                                                  fontSize: "14px",
                                                  borderBottom: "1px solid #ddd",
                                                }}
                                              >
                                                {valuer.email}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "12px",
                                                  textAlign: "center",
                                                  borderBottom: "1px solid #ddd",
                                                }}
                                              >
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-primary btn-sm me-2"
                                                  style={{ fontSize: "14px" }}
                                                  onClick={() => {
                                                    setFieldValue("valuerName", valuer.name);
                                                    setFieldValue("valuerEmail", valuer.email);
                                                  }}
                                                >
                                                  Edit
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-danger btn-sm"
                                                  style={{ fontSize: "14px" }}
                                                  onClick={() => remove(index)}
                                                >
                                                  Remove
                                                </button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-success btn-lg px-5 py-2 fw-semibold"
                disabled={isSubmitting}
                style={{ fontSize: "14px" }}
              >
                {isSubmitting ? "Saving..." : "Save Policy Configuration"}
              </button>
            </div>
          </Form>
        )}
      </Formik>

    </div>
  );
}